import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types'
import { ContentType } from '@entries/content-types'
import ContentfulFootnoteSup from '@entries/contentful-footnote-sup'
import ContentfulOfferRefresh from '@entries/contentful-offer-refresh'
import { withDisplayName } from '@hocs/with-display-name'
import { MainCta } from '@layouts/main-cta'
import { boldGreenRefresh } from '@render-mark/bold-green-refresh'
import { documentHeading1TransactionalPage } from '@render-node/document-heading-1-transactional-page'
import { documentSubHeading1TransactionalPage } from '@render-node/document-sub-heading-1-transactional-page'
import { children } from '@render-node/utils/children'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import { defaultRichTextFieldOptionsDeprecated } from '@resolvers/rich-text/default-rich-text-field-options-deprecated'
import { clsx } from 'clsx'
import { graphql } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React from 'react'

const ContentfulPageImageTitleRefreshProduct: Utils.EntryFC<
  Queries.ContentfulPageImageTitleRefreshProductFragment
> = ({
  content: { mainImage, headline, subHeadline, offerRef },
  entryInspectorProps,
  fieldInspectorProps,
}) => (
  <div
    className={clsx(
      'relative',
      'flex',
      'h-full',
      'w-full',
      'justify-center',
      'py-[72px]',
      'laptop:py-[120px]',
      '!pt-2',
      'laptop:!pt-20'
    )}
    {...entryInspectorProps()}
  >
    <div
      className={clsx(
        'grid',
        'grid-cols-1',
        mainImage && 'laptop:grid-cols-2',
        'gap-y-[24px]',
        'tablet:gap-x-[68px]',
        'group/page-image-title',
        'is-refresh-product'
      )}
    >
      <div className={clsx('flex', 'flex-col', 'text-left')}>
        <RichText
          className="text-balance"
          {...fieldInspectorProps({
            fieldId: 'headline',
          })}
          content={headline}
          options={{
            renderMark: {
              [MARKS.BOLD]: boldGreenRefresh,
            },
            renderNode: {
              [BLOCKS.DOCUMENT]: documentHeading1TransactionalPage,
              [BLOCKS.PARAGRAPH]: children,
              [INLINES.EMBEDDED_ENTRY]: {
                [ContentType.ContentfulFootnote]: ContentfulFootnoteSup,
              },
            },
          }}
        />
        {subHeadline && (
          <RichText
            {...fieldInspectorProps({
              fieldId: 'subHeadline',
            })}
            content={subHeadline}
            options={{
              ...defaultRichTextFieldOptionsDeprecated,
              renderNode: {
                ...defaultRichTextFieldOptionsDeprecated.renderNode,
                [BLOCKS.DOCUMENT]: documentSubHeading1TransactionalPage,
                [BLOCKS.PARAGRAPH]: children,
                [INLINES.EMBEDDED_ENTRY]: {
                  [ContentType.ContentfulFootnote]: ContentfulFootnoteSup,
                },
              },
            }}
          />
        )}
        <div className="w-fit">
          <div className="tablet:mb-2 tablet:mt-5">
            <MainCta />
          </div>
        </div>
        <Entry
          content={offerRef}
          mapping={{
            [ContentType.ContentfulOffer]: ContentfulOfferRefresh,
          }}
        />
      </div>
      {mainImage && (
        <div
          className="order-first laptop:order-last"
          {...fieldInspectorProps({
            fieldId: 'image',
          })}
        >
          <GatsbyImage
            alt={mainImage.description}
            className="aspect-video rounded-[32px] laptop:aspect-square"
            image={getImage(mainImage)}
            loading="eager"
          />
        </div>
      )}
    </div>
  </div>
)

export const query = graphql`
  fragment ContentfulPageImageTitleRefreshProduct on Contentful3TUjMz7TxKWiQYygwIysig {
    ...ContentfulEntry
    mainImage: image {
      ...Asset
      gatsbyImageData(
        placeholder: BLURRED
        layout: FULL_WIDTH
        quality: 70
        backgroundColor: "transparent"
        breakpoints: [300, 500, 600, 1000, 1200, 1400, 2000]
        formats: [AVIF, WEBP, AUTO]
      )
    }
    headline {
      rich
      references {
        ...ContentfulFootnoteSup
      }
    }
    subHeadline {
      rich
      references {
        ...ContentfulFootnoteSup
      }
    }
    offerRef {
      ...ContentfulOfferRefresh
    }
  }
`

export default withDisplayName(
  ContentfulPageImageTitleRefreshProduct,
  'ContentfulPageImageTitleRefreshProduct'
)
